import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CustomerService, LanguageService, TranslationService } from '@proximus/ng-common';
import {
  getPriceForOption,
  isAsOrAsasOption,
  isDisneyPlusActive,
  isDisneyPlusNotActive,
  isDisneyPlusNotEligible,
  isNetflixBuyAndSell,
  isOptionActive,
  isResOrPrepaidCustomer,
  NetflixBuyAndSellStatus,
  Option,
  OptionStatus,
} from '@dch/products-business-logic';
import { OnDestroyHookDirective } from '../hooks';
import { SPORTS_MPUID_CODES, SCARLET_SPORTS_MPUID_CODES } from 'src/app/details/constants';

@Component({
  selector: 'mprd-price',
  templateUrl: './price.component.html',
})
export class PriceComponent
  extends OnDestroyHookDirective
  implements OnInit, OnChanges
{
  @Input() option: Option;
  @Input() amountClass: string;
  @Output() isIncludedOption: EventEmitter<boolean> = new EventEmitter();
  @Input() netflixBuyAndSell: NetflixBuyAndSellStatus;
  @Input() isPending: boolean;
  @Input() showActiveStatus = true;
  @Input() showNegativeColors: boolean;
  @Input() showPriceForIncluded: boolean;
  @Input() showPriceForFree: boolean;
  public displayedPrice: any;
  public isFree: boolean;
  public isEnjoy: boolean;
  public isIncluded: boolean;
  public priceEquals: boolean;
  public priceIsZero: boolean;
  public price: any;
  public computedAmountClass: string;
  public customPriceAmountClass: string;
  public isActive: boolean;
  public enjoyLabelClass: string;
  public activeLabelClass: string;
  public warningLabelClass: string;
  public includedLabelClass: string;
  public showDisneyPlusStatus: boolean;
  public showNetflixStatus: boolean;

  public isVatIncl: boolean;
  public segmentLoaded: boolean;
  public asOfActive: boolean;

  NetflixBuyAndSellStatus = NetflixBuyAndSellStatus;
  isNetflixBuyAndSell = isNetflixBuyAndSell;
  isAsOrAsasOption = isAsOrAsasOption;
  isDisneyPlusActive = isDisneyPlusActive;
  isDisneyPlusNotActive = isDisneyPlusNotActive;
  isDisneyPlusNotEligible = isDisneyPlusNotEligible;

  constructor(
    private customerService: CustomerService,
    private languageService: LanguageService,
    private readonly translationService: TranslationService,
  ) {
    super();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['option']) {
      const previousValue = <Option>changes['option'].previousValue;
      if (
        this.option?.ibStatus &&
        this.option.ibStatus !== previousValue?.ibStatus
      ) {
        this.handleOptionChanges();
      }
    }
  }

  ngOnInit() {
    this.addSubscription(
      this.customerService.getCustomerAccount().subscribe((value) => {
        this.isVatIncl = isResOrPrepaidCustomer(value) || value.isEBU();
        this.segmentLoaded = true;
        this.handleOptionChanges();
      }),
    );
  }

  private handleOptionChanges() {
    if (this.option) {
      this.displayedPrice = getPriceForOption(this.option, this.isVatIncl);
      this.initPrice();
      this.isOptionFree();
      this.isOptionIncluded();
      this.initPriceEquals();
      this.initPriceIsZero();
      this.computeAmountClass();
      this.isOptionActive();
      this.computePriceLabelClass();
      this.isDisneyPlusOption();
      this.isNetflixOption();
      this.hasOptionEnjoy();
      this.isAsOfActive();
    }
    this.isIncludedOption.emit(this.isIncluded);
  }

  private isOptionFree() {
    // check the prices but also the customPrices to ensure an option is free or not.
    this.isFree =
      !this.showPriceForFree &&
      this.displayedPrice === 0 &&
      (!this.option?.customPrices ||
        !this.option?.customPrices?.prices ||
        this.option?.customPrices?.prices?.length === 0 ||
        this.isCustomPriceZero(this.option?.customPrices?.prices[0]));
  }

  private hasOptionEnjoy() {
    this.isEnjoy = this.option.enjoy;
  }

  private isOptionIncluded() {
    this.isIncluded =
      (this.isFree &&
        !this.option.hideIncludedLabel &&
        isOptionActive(this.option) &&
        !this.showPriceForIncluded) ||
      (this.option?.customPrices?.prices?.length === 1 &&
        this.isCustomPriceZero(this.option?.customPrices?.prices[0]));
  }

  private initPriceEquals() {
    if (this.option.price) {
      if (isOptionActive(this.option)) {
        this.priceEquals = true;
      } else if (this.isVatIncl) {
        this.priceEquals =
          this.option.price.calculatedPriceVat === undefined ||
          this.option.price.calculatedPriceVat === this.option.price.priceVat;
      } else {
        this.priceEquals =
          this.option.price.calculatedPriceNoVat === undefined ||
          this.option.price.calculatedPriceNoVat ===
            this.option.price.priceNoVat;
      }
    }
  }

  private initPriceIsZero() {
    if (this.showPriceForFree) {
      return false;
    }
    if (this.option.price) {
      if (this.isVatIncl) {
        this.priceIsZero = this.option.price.priceVat === 0;
      } else {
        this.priceIsZero = this.option.price.priceNoVat === 0;
      }
    }
  }

  private initPrice() {
    if (this.option.price) {
      if (this.isVatIncl) {
        this.price = this.option.price.priceVat;
      } else {
        this.price = this.option.price.priceNoVat;
      }
    }
  }

  private computeAmountClass() {
    if (this.amountClass) {
      this.computedAmountClass = this.amountClass;
      this.customPriceAmountClass = this.amountClass;
    }
    if (!this.priceEquals || this.isFree || this.option.enjoy) {
      this.computedAmountClass = this.computedAmountClass + ' rs-price-promo';
      if (this.showNegativeColors) {
        this.computedAmountClass =
          this.computedAmountClass + ' rs-price-semi-inverted';
      }
    } else if (this.showNegativeColors) {
      this.computedAmountClass =
        this.computedAmountClass + ' rs-price-inverted';
    }
    if (this.showNegativeColors) {
      this.customPriceAmountClass =
        this.customPriceAmountClass + ' rs-price-inverted';
    }
  }

  private isOptionActive() {
    this.isActive =
      this.option.ibStatus === OptionStatus.ACTIVE && this.showActiveStatus;
  }

  private computePriceLabelClass() {
    this.activeLabelClass = this.showNegativeColors
      ? 'rs-txt-success-neg'
      : 'rs-txt-success';
    this.warningLabelClass = this.showNegativeColors
      ? 'rs-txt-warning-neg'
      : 'rs-txt-warning';
    this.includedLabelClass = this.showNegativeColors
      ? this.amountClass + ' rs-price-inverted'
      : this.amountClass + ' rs-txt-purple';
  }

  private isCustomPriceZero(price: any) {
    return (
      (price?.priceVat === 0 || price?.priceNoVat === 0) &&
      !this.option.hideIncludedLabel
    );
  }

  private isDisneyPlusOption() {
    this.showDisneyPlusStatus =
      this.option &&
      this.option.disneyBSStatus &&
      (isDisneyPlusActive(this.option) || isDisneyPlusNotActive(this.option));
  }

  private isNetflixOption() {
    this.showNetflixStatus =
      this.option &&
      isNetflixBuyAndSell(this.option) &&
      this.netflixBuyAndSell == NetflixBuyAndSellStatus.ACTIVE;
  }


  public isAsOfActive() {
    this.asOfActive =
      this.option.optionCodeForDescription === 'DJMHG' ||
      this.option.optionCodeForDescription === 'PELYC' ||
      this.option.optionCodeForDescription === 'KOEVD' ||
      this.option.optionCodeForDescription === 'CQGHE' ||
      this.option.optionCodeForDescription === 'KGZVI' ||
      this.option.optionCodeForDescription === 'PWKON' || ((this.translationService.getTranslatedValueFor(
        'tv.isSportsFeatureFlag',
      ) ?? 'false') === 'true' &&  ( SPORTS_MPUID_CODES.includes(this.option.optionCodeForDescription) ||
            SCARLET_SPORTS_MPUID_CODES.includes(this.option.optionCodeForDescription))) ;

  }
}
